import { Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';

import { Navigate, useRoutes, useLocation } from 'react-router-dom';

// layouts
import MainLayout from 'layouts/main/MainHeader';
import LogoOnlyLayout from 'layouts/LogoOnlyLayout';

import { RequireAuth } from 'guards/RequireAuth';

// import RoleBasedGuard from '../guards/RoleBasedGuard';

import LoadingScreen from 'components/LoadingScreen';
import { FAQaccordion } from 'sections/@admin/plugins/pro_block/FAQaccordion';
import { CountDownTimer } from 'sections/@admin/plugins/ultimate_block/CountDownTimer';
import { CallToAction } from 'sections/@admin/plugins/ultimate_block/CallToAction';
import { ImageGrid } from 'sections/@dashboard/bioLinkSection/BiolinkPreviewSections/ImageGrid';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};
// ______________________________Routes_________________________________
export default function Router() {
  const linkListData = useSelector((state) => state?.linkList?.getLinkList?.response);

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: <Login />,
        },
        {
          path: 'register',
          element: <Register />,
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        {
          path: 'reset-password',
          element: <ResetPassword />,
        },
        {
          path: 'new-password/:id/:token',
          element: <NewPassword />,
        },
        {
          path: 'two-factor-auth',
          element: <TwoFactorAuthentication />,
        },
        {
          path: 'verify',
          element: <VerifyCode />,
        },
        {
          path: 'verifyemail/:id/:token',
          element: <VerifyEmail />,
        },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <RequireAuth>
          <DashboardHeader />
        </RequireAuth>
      ),
      children: [
        //  { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        // { element: <GeneralApp replace />, index: true },
        { element: <Page404 replace />, index: true },

        { path: 'links', element: <BioLinkPage /> },
        // { path: 'filelinks', element: <FileLink /> },
        { path: 'profile', element: <UserProfile /> },
      ],
    },
    {
      path: ':user',
      element: <BioLinkMainScreen linkList={linkListData} />,
    },
    // Dashboard Routes
    // {
    //   path: 'faq',
    //   element: <FAQaccordion />,
    // },
    // {
    //   path: 'email-me',
    //   element: <CallToAction />,
    // },
    // {
    //   path: 'image-grid',
    //   element: <ImageGrid />,
    // },
    {
      path: 'admin',
      element: (
        <RequireAuth>
          <SideBar />
        </RequireAuth>
      ),
      children: [
        { path: 'dashboard', element: <AdminPage /> },

        { path: 'taxes', element: <TaxesPage /> },
        { path: 'create-tax', element: <CreateTax /> },
        { path: 'edit-tax/:id', element: <EditTax /> },

        { path: 'plans', element: <BillingPage /> },
        { path: 'plansedit/:id', element: <EditPlan /> },
        { path: 'planscreate', element: <CreatePlan /> },

        { path: 'codes', element: <Codes /> },
        { path: 'codesedit/:id', element: <UpdateCodes /> },

        { path: 'payments', element: <Payments /> },
        { path: 'contact-us-message', element: <ContactUsPage /> },
        { path: 'statistics', element: <Statistics /> },
        { path: 'language', element: <Language /> },
        { path: 'plugins', element: <Plugins /> },
        { path: 'user-edit/:id', element: <UserEdit /> },
        { path: 'stripe', element: <Stripe /> },
        { path: 'users', element: <UsersPage /> },
        { path: 'view-user/:id', element: <UserView /> },
        { path: 'create-user', element: <CreateUser /> },
        { path: 'users-logs', element: <UsersLogsPage /> },
        { path: 'links', element: <LinksPage /> },
        { path: 'website-settings', element: <HomeSettings /> },

        { path: 'create-code', element: <CreateCode /> },
        { path: 'edit-code', element: <EditCode /> },

        //  { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        // { element: <GeneralApp replace />, index: true },
        // { path: 'filelinks', element: <FileLink /> },
      ],
    },
    {
      path: 'payment',
      element: (
        <RequireAuth>
          <DashboardHeader />
        </RequireAuth>
      ),
      children: [
        { path: 'type', element: <PaymentType /> },
        { path: 'plans/:id', element: <CustomPlan /> },
        { path: 'checkout', element: <Checkout /> },
        { path: 'recuringCheckout', element: <RecurringCheckout /> },
        { path: 'billing', element: <Billing /> },
        { path: 'pricing-plans', element: <PricingPlans /> },
      ],
    },
    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment1', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },
        { path: 'payment', element: <Payment /> },
        { path: 'timer', element: <CountDownTimer /> },
        { path: 'changelog', element: <Changelog /> },
        { path: 'docs', element: <Overview /> },
        { path: 'privacy', element: <Privacy /> },
        { path: 'terms-services', element: <TermsOfServices /> },
        { path: 'privacy-policy', element: <Privacy /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
// ______________________________Pages_________________________________

// AUTHENTICATION
const Login = Loadable(lazy(() => import('pages/auth/Login')));
const TwoFactorAuthentication = Loadable(lazy(() => import('pages/auth/TwoFactorAuthentication')));
const Register = Loadable(lazy(() => import('pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('pages/auth/VerifyCode')));
const VerifyEmail = Loadable(lazy(() => import('pages/auth/VerifyEmail')));
const UserProfile = Loadable(lazy(() => import('pages/dashboard/UserProfile')));
// Admin
const AdminPage = Loadable(lazy(() => import('pages/admin/AdminPage')));
const Payments = Loadable(lazy(() => import('pages/admin/PaymentsPage')));
const ContactUsPage = Loadable(lazy(() => import('pages/admin/ContactUsPage')));
const Statistics = Loadable(lazy(() => import('sections/@admin/statistics/Statistics')));
const CustomPlan = Loadable(lazy(() => import('pages/admin/CustomPlan')));
const CreatePlan = Loadable(lazy(() => import('sections/@admin/plan/CustomPlanHero')));
const EditPlan = Loadable(lazy(() => import('sections/@admin/plan/CustomPlanEdit')));
const Language = Loadable(lazy(() => import('sections/@admin/language/Language')));
const Changelog = Loadable(lazy(() => import('sections/@admin/changelog/Changelog')));
const Overview = Loadable(lazy(() => import('sections/@admin/overview/Overview')));
const TermsOfServices = Loadable(lazy(() => import('layouts/main/TermsOfServices')));
const Privacy = Loadable(lazy(() => import('layouts/main/Privacy')));

const DashboardHeader = Loadable(lazy(() => import('layouts/dashboard/header/DashBoardHeader')));
const SideBar = Loadable(lazy(() => import('layouts/admin/SideBar')));
const TaxesPage = Loadable(lazy(() => import('pages/admin/TaxesPage')));
const EditTax = Loadable(lazy(() => import('sections/@admin/taxes/EditTax')));
const CreateTax = Loadable(lazy(() => import('sections/@admin/taxes/CreateTax')));
const BillingPage = Loadable(lazy(() => import('pages/admin/BillingPage')));
const Codes = Loadable(lazy(() => import('pages/admin/Codes')));
const UpdateCodes = Loadable(lazy(() => import('sections/@admin/codes/UpdateCodes')));

const Plugins = Loadable(lazy(() => import('sections/@admin/plugins/Plugins')));
const UsersPage = Loadable(lazy(() => import('pages/admin/UsersPage')));
const UsersLogsPage = Loadable(lazy(() => import('pages/admin/UserLogs')));
const LinksPage = Loadable(lazy(() => import('pages/admin/LinksPage')));

const UserView = Loadable(lazy(() => import('sections/@admin/user/UserView')));
const UserEdit = Loadable(lazy(() => import('sections/@admin/user/UserEdit')));
const Stripe = Loadable(lazy(() => import('sections/@admin/stripe/Stripe')));
const Billing = Loadable(lazy(() => import('sections/@admin/billing/Billing')));
const PaymentType = Loadable(lazy(() => import('sections/@admin/plan/PaymentType')));
const PricingPlans = Loadable(lazy(() => import('sections/@admin/plan/PricingPlans')));
const Checkout = Loadable(lazy(() => import('sections/payment/PaymentBillingAddress')));
const RecurringCheckout = Loadable(lazy(() => import('sections/payment/PaymentBillingAddressRecuring')));
const CreateUser = Loadable(lazy(() => import('sections/@admin/user/CreateUser')));
const CreateCode = Loadable(lazy(() => import('sections/@admin/codes/CreateCode')));
const EditCode = Loadable(lazy(() => import('sections/@admin/codes/EditCode')));
const HomeSettings = Loadable(lazy(() => import('sections/@admin/HomeSettings/HomeSettings')));
// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('pages/dashboard/GeneralApp')));

// __________________________dashboard___________________
// BioLink
const BioLinkPage = Loadable(lazy(() => import('pages/dashboard/BioLinkPage')));

// _____________main screen________________
const BioLinkMainScreen = Loadable(lazy(() => import('pages/BiolnkPreview/BiolinkPreviewTab')));

// __________file link page___________
const FileLink = Loadable(lazy(() => import('pages/dashboard/FileLinkPage')));

// MAIN
const HomePage = Loadable(lazy(() => import('pages/Home')));
const About = Loadable(lazy(() => import('pages/About')));
const Contact = Loadable(lazy(() => import('pages/Contact')));
const Faqs = Loadable(lazy(() => import('pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('pages/Pricing')));
const Payment = Loadable(lazy(() => import('pages/Payment')));
const Page500 = Loadable(lazy(() => import('pages/Page500')));
const Page403 = Loadable(lazy(() => import('pages/Page403')));
const Page404 = Loadable(lazy(() => import('pages/Page404')));
